import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { Sale, CreateSaleInput, UpdateSaleInput } from "../types";

const SALE_FIELDS = `
  id
  organizationId
  saleNumber
  customerId
  warehouseId
  saleDate
  items {
    productId
    productName
    quantity
    unitPrice
    lineTotal
  }
  subtotal
  taxPercentage
  taxAmount
  total
  status
  notes
  createdAt
  updatedAt
`;

export const saleApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSales: builder.query<Sale[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Sales($organizationId: String) {
              sales(organizationId: $organizationId) {
                ${SALE_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Sale[]>(res, "sales"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: "Sale" as const, id: s.id })),
              { type: "Sale" as const, id: "LIST" },
            ]
          : [{ type: "Sale" as const, id: "LIST" }],
    }),

    getSale: builder.query<Sale, string>({
      query: (id) => ({
        body: {
          query: `
            query Sale($id: String!) {
              sale(id: $id) {
                ${SALE_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Sale>(res, "sale"),

      providesTags: (_result, _err, id) => [{ type: "Sale", id }],
    }),

    createSale: builder.mutation<Sale, CreateSaleInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateSale($input: CreateSaleInput!) {
              createSale(input: $input) {
                ${SALE_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Sale>(res, "createSale"),

      invalidatesTags: [{ type: "Sale", id: "LIST" }],
    }),

    updateSale: builder.mutation<Sale, { id: string; input: UpdateSaleInput }>({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateSale($id: String!, $input: UpdateSaleInput!) {
              updateSale(id: $id, input: $input) {
                ${SALE_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Sale>(res, "updateSale"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Sale", id },
        { type: "Sale", id: "LIST" },
      ],
    }),

    deleteSale: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteSale($id: String!) {
              deleteSale(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteSale"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Sale", id },
        { type: "Sale", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetSalesQuery,
  useLazyGetSalesQuery,
  useGetSaleQuery,
  useLazyGetSaleQuery,
  useCreateSaleMutation,
  useUpdateSaleMutation,
  useDeleteSaleMutation,
} = saleApi;
